import React from "react";
import { useState } from "react";
import useSWR from "swr";
import { Labels, Languages, Route, TimetableData } from "../types";
import { fetcher } from "../lib/fetcher";
import { JourneyList } from "./JourneyList";
import { Loader } from "./Loader";
import { addDays, isAfter, startOfDay, differenceInDays, subDays } from "date-fns";
import { DatePicker } from "./DatePicker";
import { clsx } from "clsx";

const DEFAULT_INTERVAL = 7;
const MIN_INTERVAL = 1;
const MAX_INTERVAL = 14;

export function LiveTimetable({
  labels,
  lang,
  routes,
  index,
}: {
  labels: Labels;
  lang: Languages;
  routes: Route[];
  index: number;
}) {
  const [route, setRoute] = useState<Route>(routes[0]);
  const [dates, setDates] = useState<Date[]>([
    new Date(),
    addDays(new Date(), DEFAULT_INTERVAL),
  ]);
  const [activeList, setActiveList] = useState<"outbound" | "return">(
    "outbound"
  );

  const fromDate = dates[0];
  const toDate = dates[1];

  const { data, isLoading, isValidating, error } = useSWR<TimetableData>(
    {
      from: route.fromCode,
      to: route.toCode,
      fromDate,
      toDate,
    },
    fetcher,
    {
      refreshInterval: 60000,
      keepPreviousData: true,
    }
  );

  const Heading = index === 0 ? "h2" : "h3";

  // Render two lists of journeys, one for each direction
  const journeyLists = [
    {
      id: "outbound",
      heading: `${route.fromName} - ${route.toName}`,
      journeys: data?.journeys || [],
    },
    {
      id: "return",
      heading: `${route.toName} - ${route.fromName}`,
      journeys: data?.returnJourneys || [],
    },
  ];

  return (
    <>
      <div className="live-timetable__controls">
        <div className="live-timetable__controls-col">
          <label htmlFor="route-select">{labels.route}:</label>
          <select
            onChange={(e) => {
              setRoute(routes[parseInt(e.target.value)]);
            }}
            aria-label={labels.route}
            className='live-timetable__route-select'
            id="route-select"
          >
            {routes.map((route, idx) => (
              <option key={route.fromCode + route.toCode} value={idx}>
                {route.fromName} - {route.toName}
              </option>
            ))}
          </select>
        </div>

        <div className="live-timetable__controls-col">
          <label htmlFor="from-date">{labels.fromDate}:</label>
          <DatePicker
            id="from-date"
            value={fromDate.toISOString().split("T")[0]}
            min={subDays(new Date(), 7).toISOString().split("T")[0]}
            onChange={(e) => {
              const newFromDate = new Date(e.target.value);
              let newToDate = toDate;

              if (
                !isAfter(startOfDay(toDate), startOfDay(newFromDate)) ||
                differenceInDays(toDate, newFromDate) > MAX_INTERVAL
              ) {
                newToDate = addDays(newFromDate, DEFAULT_INTERVAL);
              }

              setDates([newFromDate, newToDate]);
            }}
            aria-label={labels.fromDate}
          />
        </div>

        <div className="live-timetable__controls-col">
          <label htmlFor="to-date">{labels.toDate}:</label>
          <DatePicker
            id="to-date"
            value={toDate.toISOString().split("T")[0]}
            min={addDays(fromDate, MIN_INTERVAL).toISOString().split("T")[0]}
            max={addDays(fromDate, MAX_INTERVAL).toISOString().split("T")[0]}
            onChange={(e) => {
              setDates([fromDate, new Date(e.target.value)]);
            }}
            aria-label={labels.toDate}
          />
        </div>
      </div>

      {
        // Loader shown when fetching initial data
        isLoading && <Loader />
      }

      {error && !isLoading && !data && (
        <p className="live-timetable__error">
          {labels.errorMessage}
        </p>
      )}

      {data && !isLoading && (
        <div className="live-timetable__result">
          <div className="live-timetable__tables">
            {journeyLists.map((journeyList) => (
              <div
                className={clsx("live-timetable__table-wrapper", {
                  "live-timetable__table-wrapper--active":
                    activeList === journeyList.id,
                })}
                key={journeyList.id}
              >
                <Heading className="live-timetable__heading">
                  {journeyList.heading}

                  <button
                    type="button"
                    className="live-timetable__toggle-btn"
                    onClick={() =>
                      setActiveList(
                        activeList === "outbound" ? "return" : "outbound"
                      )
                    }
                    aria-label={
                      activeList === "outbound"
                        ? labels.showReturn
                        : labels.showOutbound
                    }
                  />
                </Heading>

                <JourneyList journeys={journeyList.journeys} labels={labels} lang={lang} />
              </div>
            ))}
          </div>

          <div className="live-timetable__footer">
            {
              // Small loader shown when there is data displayed but it's being updated in the background
              isValidating && <Loader size="sm" />
            }

            <span className="live-timetable__legend">*{labels.arrivalNextDay}</span>
          </div>
        </div>
      )}
    </>
  );
}
